import React, { FC } from "react";
import styled from "styled-components";
import { marked } from "marked";
import { Entry } from "contentful";
import CTA, { CTAProps } from "./CTA";
import { font } from "../lib/sharedStyles";

interface SubscriptionCardProps extends Entry {
    brand: string,
    fields: {
        upperContent: string;
        contentAlign: string;
        ctas?: [CTAProps];
        maxWidth?: number;
        paddingBottom?: number;
        paddingTop?: number;
        textAlign: string;
        lowerContentHeading?: string;
        lowerContentSlotOne?: string;
        lowerContentSlotTwo?: string;
        lowerContentSlotThree?: string;
        lowerContentSlotFour?: string;
        lowerContentSlotFive?: string;
        lowerContentSlotSix?: string;
        lowerContentSlotSeven?: string;
        lowerContentSlotEight?: string;
    }
}

const AlignContainer = styled.div<{ $contentAlign: string; }>`
    display: flex;
    flex-direction: column;

    ${({ $contentAlign }) => $contentAlign == 'left' && `
        align-items: flex-start;
    `}

    ${({ $contentAlign }) => $contentAlign == 'center' && `
        align-items: center;
    `}

    ${({ $contentAlign }) => $contentAlign == 'right' && `
        align-items: flex-end;
    `}
`;

interface ContainerProps {
    $maxWidth?: number;
    $paddingBottom?: number;
    $paddingTop?: number;
    $style?: string;
    $textAlign: string;
    $stackCTAs: boolean;
}

const Container = styled.div<ContainerProps>`
    position: relative;
    z-index: 1;
    height: 100%;
    width: 100%;
    transition: all .5s;
    border-top: 6px solid #6531E9;
    box-shadow: 0px 1px 20px #ccc;
    padding-left: 30px;
    padding-right: 30px;
    max-width: ${({ $maxWidth }) => $maxWidth ? `${$maxWidth}px` : '100%'};
    text-align: ${({ $textAlign }) => $textAlign || 'left'};

    ${({ $paddingTop }) => $paddingTop && `
        padding-top: ${$paddingTop}px;
    `}

    ${({ $paddingBottom }) => $paddingBottom && `
        padding-bottom: ${$paddingBottom}px;
    `}

    ${({ $stackCTAs }) => $stackCTAs && `
        > a {
        display: block;
        }
    `}
`;

const UpperContent = styled.div`
    border-bottom: 1px solid #E0E0E0;
    margin-bottom: 28px;
    padding-bottom: 10px;
    padding: 2rem;

    h3 {
        ${font('Futura', 900, '24px', '40px')}
        margin: 0;
        margin-bottom: 8px;
    }

    p {
        ${font('SourceSansPro', 500, '14px', '20px')}
        margin: 0;
        margin-bottom: 16px;
    }

    a {
        ${font('Futura', 700, '16px', '16px')}
        text-transform: uppercase;
    }
`;

const LowerContent = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    margin-top: 20px;
    text-align: left;

    h4 {
        ${font('Futura', 600, '14px', '22px')}
        margin-bottom: 8px;
        text-transform: uppercase;
    }

    ul {
        position: relative;
        list-style: none;
        margin: 0;
        padding-left: 1.2em;

        li {
            ${font('Futura', 400, '14px', '20px')}
            padding-top: 10px;
            padding-bottom: 10px;
            padding-left: 5px;

            &:before {
                content: "";
                position: absolute;
                left: 0;
                background-image: url(https://www.fmicassets.com/sites/fender.com/fender-play/check.png);
                height: 10px;
                width: 13px;
                background-size: cover;
                margin-top: 4px;
            }
        }
    }
`;

const SubscriptionCard: FC<SubscriptionCardProps> = ({ brand, fields: {
    upperContent, 
    contentAlign,
    ctas,
    maxWidth, 
    paddingBottom,
    paddingTop, 
    textAlign,
    lowerContentHeading,
    lowerContentSlotOne,
    lowerContentSlotTwo,
    lowerContentSlotThree,
    lowerContentSlotFour,
    lowerContentSlotFive,
    lowerContentSlotSix,
    lowerContentSlotSeven,
    lowerContentSlotEight,
} }) => {
    let allCTAsAreTextStyle = (ctas && ctas.length > 0) ? true : false;
    ctas?.forEach(cta => {
        if (!cta.fields.style.includes('Text')) {
        allCTAsAreTextStyle = false;
        }
    });

     const lowerContentSlots = [
        lowerContentSlotOne,
        lowerContentSlotTwo,
        lowerContentSlotThree,
        lowerContentSlotFour,
        lowerContentSlotFive,
        lowerContentSlotSix,
        lowerContentSlotSeven,
        lowerContentSlotEight
    ].filter(Boolean);

    return (
        <AlignContainer $contentAlign={contentAlign}>
        <Container 
            $maxWidth={maxWidth}
            $paddingBottom={paddingBottom}
            $paddingTop={paddingTop}
            $textAlign={textAlign}
            $stackCTAs={allCTAsAreTextStyle}
        >
            <UpperContent>
                <div dangerouslySetInnerHTML={{__html: marked.parse(upperContent)}} />
                {ctas?.map(cta => <CTA {...cta} brand={brand} />)}
            </UpperContent>
            {(lowerContentHeading || lowerContentSlots.length > 0) && (
                <LowerContent>
                    {lowerContentHeading && <h4>{lowerContentHeading}</h4>}
                    <ul>
                        {lowerContentSlots.map((slot, index) => (
                            <li key={index}>{slot}</li>
                        ))}
                    </ul>
                </LowerContent>
            )}
        </Container>
        </AlignContainer>
    );
}

export default SubscriptionCard;